import { useEffect, useRef } from 'preact/compat'
import { useDispatch, useSelector } from 'react-redux'
import { getCards } from '../api/savedCards'
import {
  cardSelect,
  cardsUpdate,
  cardsClear,
  cardsSelector,
} from '../redux/slice/cardsSlice'
import { pageStateUpdate } from '../redux/slice/pageStateSlice'
import { transactionDetailsSelector } from '../redux/slice/transactionDetailsSlice'
import { DataDog } from '../datadog'

/**
 * Loads the authenticated user's saved cards once and owns the
 * `pageState.areCardsLoaded` readiness flag.
 *
 * This used to live in the Card *button* component, which only mounts when
 * "button" is part of the components list. PaymentDetails blocks its own
 * readiness on areCardsLoaded, so any list containing "details" but not
 * "button" (e.g. components="title, methods, details") left the flag unset for
 * an authenticated user paying by card and the MFE showed the skeleton loader
 * forever. Owning it in App instead makes the flag independent of which
 * components the tenant chooses to render. See OPP-17314.
 *
 * Keyed off isUserAuthenticated rather than mount, because that value only
 * becomes known once the transaction-details response has been stored.
 *
 * Also keyed off `token`, which identifies the transaction. The hook stays
 * mounted when the tenant swaps the token to start a new transaction, and
 * useToken only clears transaction details — saved cards, the current
 * selection, the request guard and areCardsLoaded would otherwise survive into
 * the new transaction and either leak the previous user's card or block the
 * reload entirely.
 */
export default function useSavedCards({ token } = {}) {
  const dispatch = useDispatch()
  const { isUserAuthenticated } = useSelector(transactionDetailsSelector)
  const { savedCards, selectedCard } = useSelector(cardsSelector)
  const hasRequestedCards = useRef(false)
  // Transaction identity the current card state belongs to. Initialised to the
  // first token so a fresh mount is not treated as an identity change.
  const loadedForToken = useRef(token)

  useEffect(() => {
    if (loadedForToken.current !== token) {
      loadedForToken.current = token
      hasRequestedCards.current = false

      dispatch(cardsClear())
      dispatch(
        pageStateUpdate({
          areCardsLoaded: false,
        }),
      )

      // Never load on the identity change itself: isUserAuthenticated still
      // describes the previous transaction, so requesting here could pull cards
      // for a transaction whose user is not authenticated. useToken clears the
      // details on a new token, which flips the flag to false and then back to
      // true once the new details land — that re-entry performs the load.
      return
    }

    if (!isUserAuthenticated || selectedCard || hasRequestedCards.current) {
      return
    }

    hasRequestedCards.current = true

    dispatch(
      pageStateUpdate({
        areCardsLoaded: false,
      }),
    )

    // Request generation guard: a response only belongs to the token that
    // started it. A late response from a previous transaction must not
    // resurrect its cards, select one of them, or flip readiness for the
    // transaction that is now active.
    const isStale = () => loadedForToken.current !== token

    getCards()
      // API.get swallows network errors and resolves with undefined, so guard
      // the response shape: an unhandled rejection here would skip nothing but
      // still leave a noisy console, and any throw must not stop the finally
      // below from releasing the readiness flag.
      .then(response => {
        if (isStale()) {
          return
        }

        const responseData = response?.responseData

        if (responseData?.length > 0 && !savedCards.length) {
          dispatch(cardsUpdate(responseData))
          dispatch(
            cardSelect(
              responseData.find(card => card?.isDefault)?.id ||
                responseData[0].id,
            ),
          )
        }
      })
      .catch(error => {
        if (isStale()) {
          return
        }

        DataDog.error('Failed to load saved cards', error)
      })
      .finally(() => {
        if (isStale()) {
          return
        }

        dispatch(
          pageStateUpdate({
            areCardsLoaded: true,
          }),
        )
      })
  }, [token, isUserAuthenticated])
}
